import {useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";

import {AlseasonsShell} from "@components/alseasons/AlseasonsShell";
import {ContentArea} from "@components/layout/ContentArea";
import {TopBar} from "@components/layout/TopBar";
import {WorkspaceShell} from "@components/vnext/WorkspaceShell";
import {useIntegrations} from "@hooks/use-integrations";
import {useStaffingMarginAvailability} from "@hooks/use-staffing-margin";

// The SaaS Summary tab ships synthetic demo data, so it is restricted to the
// demo tenant by name (Xero tenant IDs rotate; the name is the stable key).
const DEMO_TENANT_NAME = "Demo Company (AU)";
const BASE_DASHBOARD_TABS = ["ceo_summary", "xero_pnl", "cashflow_summary", "bs_summary", "bookings_summary"];
// saas_summary is demo-only above; it must never appear in the Financial Model view.
const FINANCIAL_MODEL_EXCLUDED_TABS = [...BASE_DASHBOARD_TABS, "saas_summary", "bookings_summary"];
type TopTab = "dashboard" | "financial" | "alseasons";

interface WorkspacePageProps {
    initialTab?: TopTab;
}

export function WorkspacePage({initialTab}: WorkspacePageProps) {
    const location = useLocation();
    const staffingMargin = useStaffingMarginAvailability();
    const {getIntegration} = useIntegrations();

    const selectedTenantName = getIntegration("xero")?.xeroTenantSelected?.tenantName ?? "";
    const showSaasSummary = selectedTenantName.trim().toLowerCase() === DEMO_TENANT_NAME.toLowerCase();
    const dashboardTabs = useMemo(
        () =>
            showSaasSummary
                ? [BASE_DASHBOARD_TABS[0], "saas_summary", ...BASE_DASHBOARD_TABS.slice(1)]
                : BASE_DASHBOARD_TABS,
        [showSaasSummary]
    );

    const [topTab, setTopTab] = useState<TopTab>(() => {
        if (initialTab) return initialTab;
        if (location.pathname.startsWith("/alseasons")) return "alseasons";
        return location.pathname.startsWith("/financialmodel") ? "financial" : "dashboard";
    });

    // Sync URL when tab changes (without React Router navigation)
    useEffect(() => {
        const targetPath =
            topTab === "financial"
                ? "/financialmodel"
                : topTab === "alseasons"
                  ? "/alseasons"
                  : "/dashboard";
        if (location.pathname !== targetPath) {
            window.history.replaceState(null, "", targetPath);
        }
    }, [topTab, location.pathname]);

    // If Alseasons is not enabled for this account, never sit on its tab.
    useEffect(() => {
        if (!staffingMargin.isLoading && topTab === "alseasons" && !staffingMargin.enabled) {
            setTopTab("dashboard");
        }
    }, [staffingMargin.enabled, staffingMargin.isLoading, topTab]);

    // Dispatch tab-changed event so agents/chat know
    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent("dashboard-tab-changed", {detail: {tab: topTab}})
        );
    }, [topTab]);

    return (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <TopBar topTab={topTab} onTabChange={setTopTab} showAlseasons={staffingMargin.enabled} />
            <ContentArea>
                {topTab === "dashboard" ? (
                    <WorkspaceShell
                        onlyTabs={dashboardTabs}
                        pageTitle="Dashboard"
                        defaultTab="ceo_summary"
                    />
                ) : topTab === "financial" ? (
                    <WorkspaceShell excludeTabs={FINANCIAL_MODEL_EXCLUDED_TABS} />
                ) : (
                    <AlseasonsShell />
                )}
            </ContentArea>
        </div>
    );
}
